import { DeleteRounded, EditRounded, LinkRounded, OpenInNewRounded, SyncRounded } from '@mui/icons-material';
import { Box, Button, Chip, IconButton, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { TableComponent } from 'app/shared/components/table-component/TableComponent';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { DEFAULT_ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';
import React, { useCallback, useEffect, useState } from 'react';
import { getSortState, translate } from 'react-jhipster';
import { Link, RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { clearEntities, getEntities, ICameraDevice } from './camera-device.reducer';
import { CameraDeviceDeleteDialog } from './CameraDeviceDeleteDialog';

const deviceStatusLabel = (status?: number | null) => {
  if (status === 1) return 'Online';
  if (status === 0) return 'Offline';
  if (status === 2) return 'Em repouso';
  return 'Desconhecido';
};

const CameraDevice = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { match } = props;

  const devices = useAppSelector(state => state.cameraDevice.entities) as ICameraDevice[];
  const loading = useAppSelector(state => state.cameraDevice.loading);
  const totalItems = useAppSelector(state => state.cameraDevice.totalItems) || 0;
  const [deviceToDelete, setDeviceToDelete] = useState<ICameraDevice | null>(null);

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'id'), props.location.search)
  );

  const getAllEntities = useCallback(() => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  }, [paginationState, dispatch]);

  useEffect(() => {
    dispatch(clearEntities());
    getAllEntities();
  }, [getAllEntities, dispatch]);

  const handlePagination = (_, page: number) => {
    setPaginationState(prev => ({ ...prev, activePage: page }));
  };

  const handleSync = () => {
    dispatch(clearEntities());
    getAllEntities();
  };

  const goToEdit = (device: ICameraDevice) => props.history.push(`/camera-device/${device.mappingId}/edit`);

  const goToLink = (device: ICameraDevice) => {
    const params = new URLSearchParams();
    if (device.serialNumber) {
      params.set('serialNumber', device.serialNumber);
    }
    props.history.push(`/camera-device/new${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const deviceStatusChipSx = (status?: number | null) => {
    if (status === 1) {
      return { color: theme.palette.success.main, backgroundColor: alpha(theme.palette.success.main, 0.12) };
    }
    if (status === 0) {
      return { color: theme.palette.text.secondary, backgroundColor: alpha(theme.palette.text.secondary, 0.12) };
    }
    if (status === 2) {
      return { color: theme.palette.warning.main, backgroundColor: alpha(theme.palette.warning.main, 0.12) };
    }
    return { color: theme.palette.text.secondary, backgroundColor: theme.palette.action.selected };
  };

  const headers: IHeaderItem[] = [
    { key: 'deviceStatus', sortable: false, component: <>Status</> },
    { key: 'companyName', sortable: false, component: <>{translate('fleetSenseApp.cameraDevice.company')}</> },
    { key: 'licensePlate', sortable: false, component: <>{translate('fleetSenseApp.cameraDevice.truckImplement')}</> },
    { key: 'serialNumber', sortable: false, component: <>Número de série</> },
    { key: 'terminalID', sortable: false, component: <>ID do terminal</> },
    { key: 'deviceModelCode', sortable: false, component: <>Modelo</> },
    { key: 'createTime', sortable: false, component: <>Data Criação</> },
    { key: 'version', sortable: false, component: <>Versão</> },
  ];

  const data: IListData[] = [
    {
      type: 'child',
      value: 'deviceStatus',
      renderTableChild: device => (
        <Chip
          size="small"
          label={deviceStatusLabel(device.deviceStatus)}
          sx={{
            ...deviceStatusChipSx(device.deviceStatus),
            fontWeight: 600,
            '& .MuiChip-label': {
              px: 1,
            },
          }}
        />
      ),
    },
    {
      type: 'child',
      value: 'companyName',
      renderTableChild: device => (
        <Typography color="primary.contrastText" fontSize="0.875rem">
          {device.companyName || '—'}
        </Typography>
      ),
    },
    {
      type: 'child',
      value: 'licensePlate',
      renderTableChild: device => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography color="primary.contrastText" fontSize="0.875rem">
            {device.licensePlate || '—'}
          </Typography>
          <Tooltip title="Abrir detalhes do veículo">
            <span>
              <IconButton
                color="secondary"
                component={Link}
                to={device.truckImplementId ? `/dashboards/truck-implement/${device.truckImplementId}` : '#'}
                size="small"
                disabled={!device.truckImplementId}
                aria-label="Abrir detalhes do veículo"
              >
                <OpenInNewRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
    { type: 'text', value: 'serialNumber' },
    {
      type: 'child',
      value: 'terminalID',
      renderTableChild: device => (
        <Typography color="primary.contrastText" fontSize="0.875rem">
          {device.terminalID || '—'}
        </Typography>
      ),
    },
    { type: 'text', value: 'deviceModelCode' },
    {
      type: 'child',
      value: 'createTime',
      renderTableChild: device => (
        <Typography color="primary.contrastText" fontSize="0.875rem">
          {device.createTime || '—'}
        </Typography>
      ),
    },
    {
      type: 'child',
      value: 'version',
      renderTableChild: device => (
        <Typography color="primary.contrastText" fontSize="0.875rem">
          {device.version || '—'}
        </Typography>
      ),
    },
    {
      type: 'child',
      value: 'actions',
      renderTableChild: device =>
        device.mappingId ? (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Editar">
              <IconButton size="small" data-cy="entityEditButton" onClick={() => goToEdit(device)}>
                <EditRounded color="secondary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton size="small" data-cy="entityDeleteButton" onClick={() => setDeviceToDelete(device)}>
                <DeleteRounded color="error" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : device.serialNumber ? (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<LinkRounded />}
            data-cy="entityLinkButton"
            onClick={() => goToLink(device)}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Vincular
          </Button>
        ) : null,
    },
  ];

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', padding: '24px' }}>
      <PageHeader
        headingId="camera-device-heading"
        headingDataCy="CameraDeviceHeading"
        contentKey="fleetSenseApp.cameraDevice.home.title"
        buttonTextKey="fleetSenseApp.cameraDevice.home.createLabel"
        showButton
        urlButton="/camera-device/new"
        match={match}
        extraActions={
          <Button
            color="secondary"
            variant="outlined"
            startIcon={<SyncRounded />}
            onClick={handleSync}
            disabled={loading}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              height: '42px',
              marginBottom: { xs: '0.35em', sm: '0' },
            }}
          >
            Sincronizar
          </Button>
        }
      />

      <Box sx={{ mt: 2 }}>
        {!loading && devices.length > 0 && <TableComponent listData={data} headers={headers} list={devices} match={match} pagination={paginationState} />}

        {!loading && devices.length === 0 && <NoData title={translate('fleetSenseApp.cameraDevice.home.notFound')} />}

        {loading && <Loading />}

        <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={devices} />
      </Box>
      <CameraDeviceDeleteDialog
        mappingId={deviceToDelete?.mappingId ?? null}
        serialNumber={deviceToDelete?.serialNumber}
        open={deviceToDelete !== null}
        onClose={() => setDeviceToDelete(null)}
        onDeleted={() => {
          toast.success('Dispositivo excluído com sucesso.');
          getAllEntities();
        }}
      />
    </Box>
  );
};

export default CameraDevice;
